import { useState } from "react"
import { Form, Formik } from "formik"
import { Alert, Button, Modal, Spinner } from "react-bootstrap"
import { Link } from "react-router-dom"
import * as yup from 'yup'
import { useBulkEditBookingsMutation, useEditBookingMutation } from "../../../services/bClientApi"
import { convertStringToJSON } from "../../../Helpers/Strings/stringHelpers"
import useMyProperties from "../../../Hooks/UseMyProperties/UseMyPropertiesHook"
import useRelatedBookings from "../../../Hooks/UseRelatedBookings/UseRelatedBookings"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { isParent } from "../../../Helpers/Property/isParent"
import { SelectInputField } from "../../FormFields/SelectInput/SelectInputField"

const VRBO_LINKED_SERVICES = ['vrbo-integration', 'vrbo-integration-child']

const CANCELLATION_SOURCE_OPTIONS = [
  { value: 'owner', text: 'Owner' },
  { value: 'guest', text: 'Guest' },
]

export default function CancelBookingModal(props: {
  active: boolean,
  booking: PropertyBookingEntry,
  clear: () => void,
}) {
  const { active, booking, clear } = props
  const [error, setError] = useState('')
  const [editBooking, editState] = useEditBookingMutation()
  const [bulkEditBookings, bulkEditState] = useBulkEditBookingsMutation()
  const { myProperties } = useMyProperties()
  const relatedBookings = useRelatedBookings(booking?._id, booking, myProperties)

  const childBookings = relatedBookings.filter(relatedBooking => (
    !isParent(myProperties[relatedBooking.propertyId], myProperties[booking.propertyId])
  ))

  const requiresCancellationSource = booking?.source === 'vrbo' && (
    hasVrboIntegration(booking) || childBookings.some(hasVrboIntegration)
  )

  const loading = editState.isLoading || bulkEditState.isLoading

  const validationSchema = yup.object().shape({
    cancellationSource: requiresCancellationSource
      ? yup.string().required('Please select who is canceling this booking')
      : yup.string(),
  })

  const handleClose = () => {
    if (loading) return
    setError('')
    clear()
  }

  const handleConfirm = async (values: { cancellationSource: string }) => {
    setError('')
    const cancelReason = requiresCancellationSource
      ? (values.cancellationSource === 'guest' ? 'CANCELLED_BY_TRAVELER' : 'CANCELLED_BY_OWNER')
      : undefined

    try {
        if (!booking || !booking._id) {
            throw new Error('Booking ID is missing')
        }
      if (childBookings.length > 0) {
        const bookingsToEdit = [
          { _id: booking._id, ...buildCancelPayload(booking, cancelReason) },
          ...childBookings.map(childBooking => ({
            _id: childBooking._id,
            ...buildCancelPayload(childBooking, cancelReason),
          })),
        ]
        await bulkEditBookings(bookingsToEdit).unwrap()
      } else {
        await editBooking({
          bookingId: booking._id,
          bookingInfo: buildCancelPayload(booking, cancelReason),
        }).unwrap()
      }
      clear()
    } catch (requestError: any) {
      setError(requestError?.data?.error || 'Unable to cancel booking')
    }
  }

  return (
    <Modal show={active} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Cancel Booking</Modal.Title>
      </Modal.Header>
      <Formik
        key={`${active}-${booking?._id || ''}`}
        initialValues={{ cancellationSource: '' }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            await handleConfirm(values)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ values, isSubmitting }) => {
          const cancelDisabled = requiresCancellationSource && !values.cancellationSource

          return (
            <Form>
              <Modal.Body>
                {loading && (
                  <div className="text-center mb-3"><Spinner animation="border" size="sm" /></div>
                )}
                {error && <Alert variant="danger">{error}</Alert>}

                <p>Are you sure you want to cancel this booking?</p>

                {childBookings.length > 0 && (
                  <div className="related-bookings mt-2 mb-3">
                    <b>Child bookings that will also be canceled</b>
                    <div>
                      {childBookings.map(childBooking => {
                        const propertyInfo = myProperties[childBooking.propertyId]
                        return (
                          <div key={childBooking._id}>
                            <Link to={`/booking/${childBooking._id}`}>
                              {propertyInfo?.title || childBooking.propertyId}
                            </Link>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {requiresCancellationSource && (
                  <SelectInputField
                    fieldName="cancellationSource"
                    fieldLabel="Who is canceling this booking?"
                    placeholder="Select cancellation source"
                    required
                    disabled={loading}
                    options={CANCELLATION_SOURCE_OPTIONS}
                  />
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" type="button" onClick={handleClose} disabled={loading}>Nevermind</Button>
                <Button
                  variant="danger"
                  type="submit"
                  disabled={loading || isSubmitting || cancelDisabled}
                >
                  {loading ? 'Canceling...' : 'Confirm Cancellation'}
                </Button>
              </Modal.Footer>
            </Form>
          )
        }}
      </Formik>
    </Modal>
  )
}

function hasVrboIntegration(targetBooking: Partial<PropertyBookingEntry>) {
  return targetBooking?.createdBy === 'server_vrbo_olb' || (targetBooking?.linkedServices || []).some(service => (
    VRBO_LINKED_SERVICES.includes(service.linkedService)
  ))
}

function buildCancelPayload(targetBooking: Partial<PropertyBookingEntry>, cancelReason?: string) {
  const payload: { bookingStatus: string, linkedServices?: any[] } = {
    bookingStatus: 'canceled',
  }

  const linkedServices = targetBooking?.linkedServices || []

  if (cancelReason && hasVrboIntegration(targetBooking)) {
    payload.linkedServices = linkedServices.map((service) => {
      if (VRBO_LINKED_SERVICES.includes(service.linkedService)) {
        const parsedLinkedServiceId = convertStringToJSON(service.linkedServiceId)
        return {
          ...service,
          linkedServiceId: JSON.stringify({
            ...parsedLinkedServiceId,
            cancelReason,
          }),
        }
      }
      return service
    })
  }

  return payload
}
